import {Component, OnInit, ViewChild} from '@angular/core';
import {
  ShopSellerBatchChangeHistoryModel,
  ShopSellerEntityChangeHistoryModel,
  ShopSellerInformationModel
} from '../../../core/models/shop-seller/shop-seller.model';
import {ShopSellerService} from '../../../core/services/shop-seller/shop-seller.service';
import {ActivatedRoute} from '@angular/router';
import {HttpErrorResponse} from '@angular/common/http';
import {getHttpErrorMessage} from '../../../core/helpers/http-error-response.helper';
import {AlertComponent} from '../../../shared/components/alert/alert.component';
import {LoadingComponent} from '../../../shared/components/loading/loading.component';
import {EditorComponent, MonacoEditorModule} from 'ngx-monaco-editor-v2';
import {editor, json} from 'monaco-editor';
import IStandaloneCodeEditor = editor.IStandaloneCodeEditor;
import {FormsModule} from '@angular/forms';
import {GanCashierInfo, GanSellerBatchItem} from '../../../core/models/shop-seller/shop-seller-gan.model';
import {ButtonComponent} from '../../../shared/components/button/button.component';
import {AccordionComponent} from '../../../shared/components/accordion/accordion.component';
import {AccordionItemComponent} from '../../../shared/components/accordion/accordion-item.component';
import {
  createPaginationState,
  setPaginationLoading,
  updatePaginationFromResult,
} from '../../../core/helpers/pagination-state.helper';
import {TranslateModule} from '@ngx-translate/core';
import {CommonModule} from '@angular/common';
import {PaginationComponent} from '../../../shared/components/pagination/pagination.component';
import {ModalComponent} from '../../../shared/components/modal/modal.component';

export const CashierInfoSchema = {
  $id: 'inmemory://schemas/CashierInfo.json',
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  additionalProperties: false,
  required: ['number', 'storeId'],
  properties: {
    number: { type: 'integer' },
    storeId: { type: 'string' }
  }
} as const;

export const SellerBatchItemSchema = {
  $id: 'inmemory://schemas/SellerBatchItem.json',
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  additionalProperties: false,
  required: [
    'authorized',
    'cashier',
    'email',
    'enabledNewCart',
    'expeditionPending',
    'goldenProducts',
    'group',
    'isPinPadRemoteSale',
    'login',
    'message',
    'name',
    'priceList',
    'productLines',
    'role',
    'salesMan',
    'sellerType',
    'storeId'
  ],
  properties: {
    authorized: { type: 'boolean' },
    cashier: {
      type: 'array',
      items: { $ref: 'inmemory://schemas/CashierInfo.json' }
    },
    email: { type: 'string', format: 'email' },
    enabledNewCart: { type: 'boolean' },
    expeditionPending: { type: 'boolean' },
    goldenProducts: { type: 'boolean' },
    group: { type: 'string' },
    isPinPadRemoteSale: { type: 'boolean' },
    login: { type: 'string' },
    message: { type: 'string' },
    name: { type: 'string' },
    priceList: {
      type: 'array',
      items: { type: 'string' }
    },
    productLines: {
      type: 'array',
      items: { type: 'string' }
    },
    role: { type: 'string' },
    salesMan: { type: 'string' },
    sellerType: { type: 'string' },
    storeId: { type: 'string' }
  }
} as const;

@Component({
  selector: 'app-shop-seller-view',
  standalone: true,
  imports: [
    AlertComponent,
    LoadingComponent,
    EditorComponent,
    MonacoEditorModule,
    FormsModule,
    ButtonComponent,
    AccordionComponent,
    AccordionItemComponent,
    TranslateModule,
    CommonModule,
    PaginationComponent,
    ModalComponent
  ],
  templateUrl: './shop-seller-view.component.html',
  styleUrl: './shop-seller-view.component.scss'
})
export class ShopSellerViewComponent implements OnInit {

  /** Numeric id (database source) or master data document id (master data source). */
  sellerId!: number | string;
  sellerInfo: ShopSellerInformationModel | null = null;
  loading: boolean = false;
  workingLoading: boolean = false;
  error: string | null = null;

  sellerInfoData: string = "{}";

  editorOptions = {
    language: 'json',
    automaticLayout: true,
    theme: 'vs-dark',            // <- dark theme
    formatOnPaste: true,         // auto-format on paste
    formatOnType: true           // auto-format while typing
  }

  modalTitle = '';
  modalError: string | null = null;
  modalIsLoading = false;
  diffOptions = {
    readOnly: true,
    theme: 'vs-dark',
    automaticLayout: true,
    renderSideBySide: true,
    enableSplitViewResizing: true,
    ignoreTrimWhitespace: false,
    renderIndicators: true,
    hideUnchangedRegions: true
  };
  modalOriginalModel: { language: string, code: string } | null = null;
  modalModifiedModel: { language: string, code: string } | null = null;

  @ViewChild('entityModal') entityModalRef!: ModalComponent;
  userChangeHistory: ShopSellerEntityChangeHistoryModel[] = [];
  readonly historyPaginationState = createPaginationState();

  batchChangeHistory: ShopSellerBatchChangeHistoryModel[] = [];
  readonly batchHistoryPaginationState = createPaginationState();

  constructor(
    private readonly shopSellerService: ShopSellerService,
    private readonly route: ActivatedRoute,
  ) {
  }

  ngOnInit(): void {
    this.error = null;
    this.loading = true;

    const tmpId = this.route.snapshot.paramMap.get('sellerId');
    if (!tmpId) {
      this.loading = false;
      return;
    }

    this.sellerId = /^\d+$/.test(tmpId) ? Number(tmpId) : tmpId;
    this.shopSellerService.getSellerById(this.sellerId).subscribe({
      next: (result: ShopSellerInformationModel) => {
        this.sellerInfo = result;

        const sellerGanData: GanSellerBatchItem = this.toSellerGanData(result);

        this.sellerInfoData = this.prettifyJson(JSON.stringify(sellerGanData));

        this.onUserHistoryPageChange(0, this.historyPaginationState().itemsPerPage);
        this.onBatchHistoryPageChange(0, this.batchHistoryPaginationState().itemsPerPage);
        this.loading = false;
      },
      error: (error: HttpErrorResponse) => {
        console.log(error);
        this.error = getHttpErrorMessage(error);
        this.loading = false;
      }
    });
  }

  toSellerGanData(result: ShopSellerInformationModel): GanSellerBatchItem {
    return {
      Authorized: result.authorized,
      Cashier: result.sellerCashiers.map(x => {
        const ganCashier: GanCashierInfo = {
          Number: x.number,
          StoreId: x.storeId
        };
        return ganCashier;
      }),
      Email: result.email,
      EnabledNewCart: result.enabledNewCart,
      ExpeditionPending: result.expeditionPending,
      GoldenProducts: result.goldenProducts,
      Group: result.group,
      IsPinPadRemoteSale: result.isPinPadRemoteSale,
      Login: result.login,
      Message: '',
      Name: result.name,
      PriceList: result.priceList,
      ProductLines: result.productLines,
      Role: result.role,
      SalesMan: result.salesMan,
      SellerType: result.sellerType,
      StoreId: result.storeId
    };
  }

  onUserHistoryPageChange = (page: number, pageSize: number): void => {
    if (!this.sellerInfo?.email) return;

    setPaginationLoading(this.historyPaginationState, true);
    this.shopSellerService.getVtexSellerEntityHistory(this.sellerInfo.email, page, pageSize).subscribe({
      next: (result) => {
        this.userChangeHistory = result.content;
        updatePaginationFromResult(this.historyPaginationState, result, pageSize);
      },
      error: (error: HttpErrorResponse) => {
        this.error = getHttpErrorMessage(error);
        setPaginationLoading(this.historyPaginationState, false);
      }
    });
  };

  onBatchHistoryPageChange = (page: number, pageSize: number): void => {
    if (!this.sellerInfo?.email) return;

    setPaginationLoading(this.batchHistoryPaginationState, true);
    this.shopSellerService.getVtexSellerBatchHistory(this.sellerInfo.email, page, pageSize).subscribe({
      next: (result) => {
        this.batchChangeHistory = result.content;
        updatePaginationFromResult(this.batchHistoryPaginationState, result, pageSize);
      },
      error: () => {
        setPaginationLoading(this.batchHistoryPaginationState, false);
      }
    });
  };

  onEditorInit(editor: IStandaloneCodeEditor) {
    const model = editor.getModel();
    if (!model) {
      return;
    }

    json.jsonDefaults.setDiagnosticsOptions({
      validate: true,
      allowComments: false,
      trailingCommas: `error`,
      comments: `error`,
      schemaValidation: `error`,
      schemas: [
        {
          uri: SellerBatchItemSchema.$id,
          fileMatch: [model.uri.toString()],
          schema: SellerBatchItemSchema
        },
        {
          uri: CashierInfoSchema.$id,
          fileMatch: ['*'], // make $ref resolvable
          schema: CashierInfoSchema
        }
      ]
    });
  }

  private prettifyJson(text: string): string {
    try {
      return JSON.stringify(JSON.parse(text), null, 2);
    } catch {
      return text;
    }
  }

  doSyncRequest() {
    this.workingLoading = true;
    this.error = null;
    this.shopSellerService.postSyncSeller(Number(this.sellerId)).subscribe({
      next: () => {
        // reload page
        this.ngOnInit();
        this.workingLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        console.log(error);
        this.error = getHttpErrorMessage(error);
        this.workingLoading = false;
      }
    });
  }

  doVtexCreationRequest() {
    this.workingLoading = true;
    this.error = null;

    this.shopSellerService.postCreateVtexSeller(Number(this.sellerId), JSON.parse(this.sellerInfoData)).subscribe({
      next: () => {
        this.ngOnInit();
        this.workingLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        console.log(error);
        this.error = getHttpErrorMessage(error);
        this.workingLoading = false;
      }
    });
  }

  doVtexUpdateRequest() {
    this.workingLoading = true;
    this.error = null;

    this.shopSellerService.putUpdateVtexSeller(this.sellerId, JSON.parse(this.sellerInfoData)).subscribe({
      next: () => {
        this.ngOnInit();
        this.workingLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        console.log(error);
        this.error = getHttpErrorMessage(error);
        this.workingLoading = false;
      }
    });
  }

  doVtexDeletionRequest() {
    this.workingLoading = true;
    this.error = null;

    this.shopSellerService.deleteVtexSeller(this.sellerId).subscribe({
      next: () => {
        this.ngOnInit();
        this.workingLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        console.log(error);
        this.error = getHttpErrorMessage(error);
        this.workingLoading = false;
      }
    });
  }

  operationType(op: string): string {
    switch (op) {
      case 'ADD':
      case 'ADDED':
        return 'Criação';
      case 'MODIFIED':
      case 'UPD':
        return 'Atualização';
      case 'DEL':
      case 'DELETED':
        return 'Exclusão';
      case 'NOP':
        return 'Inalterado';
      default:
        return 'UNKNOWN';
    }
  }

  onOpenBatchModal(model: ShopSellerBatchChangeHistoryModel): void {
    this.onOpenModal();

    this.modalTitle = `Lote ${model.id} - ${model.date}`;
    this.modalOriginalModel = {
      language: 'json',
      code: this.prettifyJson(JSON.stringify(JSON.parse(model.previousPayload || '{}'))),
    };
    this.modalModifiedModel = {
      language: 'json',
      code: this.prettifyJson(JSON.stringify(JSON.parse(model.payload))),
    };

    this.modalIsLoading = false;
  }

  onOpenEntityModal(model: ShopSellerEntityChangeHistoryModel): void {
    this.onOpenModal();

    this.modalTitle = `Entidade - Auditoria ${model.auditId} - ${model.date}`;
    this.modalOriginalModel = {
      language: 'json',
      code: this.prettifyJson(JSON.stringify(model.entityBefore))
    };
    this.modalModifiedModel = {
      language: 'json',
      code: this.prettifyJson(JSON.stringify(model.entityAfter)),
    };

    this.modalIsLoading = false;
  }

  onOpenModal() {
    this.modalIsLoading = true;
    this.entityModalRef.openDialog();
  }
}
